/**
 * Word-extraction shared by `SentenceInput` and the home route's submit
 * gate. We split on whitespace, strip non-letter characters, and
 * lowercase. The BIP-39 wordlist is lowercase a–z only, so anything
 * outside that range can't be a valid word — but we tolerate it in the
 * input and just flag the resulting empty/garbled tokens.
 */
export function extractWords(sentence: string): string[] {
  return sentence
    .split(/\s+/)
    .map((raw) => raw.toLowerCase().replace(/[^a-z]/g, ""))
    .filter((w) => w.length > 0);
}

/**
 * Given the full sentence and a cursor offset, returns the bounds of the
 * "word currently being typed" — the run of letters around the cursor.
 * Used to drive autocomplete.
 */
export interface CurrentWord {
  readonly start: number;
  readonly end: number;
  readonly partial: string;
}

export function getCurrentWord(text: string, cursor: number): CurrentWord {
  const isWordChar = (ch: string | undefined): boolean =>
    ch !== undefined && /[a-zA-Z]/.test(ch);
  let start = cursor;
  while (start > 0 && isWordChar(text[start - 1])) start--;
  let end = cursor;
  while (end < text.length && isWordChar(text[end])) end++;
  return { start, end, partial: text.slice(start, end).toLowerCase() };
}
